import { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import useRetryFetch from "../hooks/useRetryFetch";
import useAuth from "../hooks/useAuth";
import { tryGetUserRoles } from "../api/auth";
import LoggerContext from "../context/LoggerProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { directionService } from "../services/directionService";
import { Link } from "react-router-dom";
import "./Login.css";

import signInGoogle from "../images/SignInGoogle.png";
import loadingImage from "../images/loading.gif";

import { config } from "../Environment";
import { WSContext } from "../App";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

import PresListContext from "../context/PresListProvider";

function Login() {
  const { setAuth } = useAuth();
  const { Logger } = useContext(LoggerContext);
  const retryFetch = useRetryFetch();

  const emailRef = useRef();
  const { t } = useTranslation();
  const { socket } = useContext(WSContext);
  const { setPrescriptions } = useContext(PresListContext);

  const navigate = useNavigate();
  const location = useLocation();
  const fromPathname = location.state?.from?.pathname || "/portal";
  const fromSearch = location.state?.from?.search || "";
  const fromHash = location.state?.from?.hash || "";
  const acceptId = location.state?.acceptId;

  const [authMethodsReady, setAuthMethodsReady] = useState(false);
  const [authMethods, setAuthMethods] = useState([]);
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [direction, setDirection] = useState(document.body.dir);

  async function getAuthMethods() {
    const response = await fetch(config.API_BASE + "/api/auth/methods");
    if (response.ok) {
      const responseJSON = await response.json();
      if (Array.isArray(responseJSON.list)) {
        return responseJSON.list;
      }
    }
    return [];
  }

  async function authenticateAndRedirect({ email, accessToken }) {
    Logger.info("Login.jsx authenticateAndRedirect");
    // load user's roles
    setAuth({ email, accessToken, roles: undefined, impersonatedBy: undefined }); // accessToken must be set to call roles APi
    localStorage.setItem("auth", JSON.stringify({ email, accessToken }));
    const loadedRoles = await tryGetUserRoles(retryFetch);
    if (!Array.isArray(loadedRoles)) {
      setErrMsg(t("msg_error_failed_loading_user_roles"));
    }
    const roles = Array.isArray(loadedRoles) ? loadedRoles : [];

    setAuth({ email, accessToken, roles, impersonatedBy: undefined });
    console.log(`Login.jsx storing auth ${email} with roles ${roles}`);
    localStorage.setItem("auth", JSON.stringify({ email, accessToken, roles }));

    // send login message
    socket.send(
      JSON.stringify({
        command: "login",
        payload: {
          email,
        },
      })
    );

    setPrescriptions(null); // delete possible public prescriptions after login

    if (acceptId) {
      navigate(`/accept/${acceptId}?t=${Date.now()}`, { replace: true });
    } else if (sessionStorage.getItem("draftprescription")) {
      navigate(`/design?t=${Date.now()}`);
    } else {
      console.log(`Login.jsx returning to prior activity: ${fromPathname} with search ${fromSearch} and hash ${fromHash}`);
      if (["/register", "/login"].includes(fromPathname)) {
        navigate(`/portal?t=${Date.now()}`, { replace: true });
      } else {
        const prefix = fromSearch ? '&' : '?';
        const timestamp = `${prefix}t=${Date.now()}`;
        navigate(`${fromPathname}${fromSearch}${timestamp}${fromHash}`, { replace: true });
      }
    }
  }

  useEffect(() => {
    if (location.state?.email && location.state?.accessToken) {
      Logger.info("Login.jsx got email and access token");
      setEmail(location.state?.email);
      setTimeout(() => {
        authenticateAndRedirect({ email: location.state?.email, accessToken: location.state?.accessToken });
      }, 10);
    }
  }, []);

  useEffect(() => {
    (async function displayAuthMethods() {
      const result = await getAuthMethods();
      setAuthMethods(result);
      setAuthMethodsReady(true);
    })();
  }, []);

  useEffect(() => {
      if (authMethodsReady && authMethods.includes('password')) {
        emailRef.current.focus();
      }
  }, [authMethods, authMethodsReady]);

  useEffect(() => {
    let directionUnsubscribe = directionService
      .getDirectionChange()
      .subscribe(() => {
        setDirection(document.body.dir);
      });

    return () => {
      directionUnsubscribe.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [email, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !pwd) {
      emailRef.current.focus();
      return setErrMsg(t("msg_error_email_and_password_required"));
    }

    let response = await fetch(config.API_BASE + "/api/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        password: pwd,
      }),
      credentials: "include",
    });
    if (!response.ok) {
      setErrMsg(t("msg_error_email_or_password_not_correct"));
      emailRef.current.focus();
      return;
    }
    
    setEmail("");
    setPwd("");
    let data = await response.json();

    let accessToken = data?.accessToken;

    await authenticateAndRedirect({ email, accessToken });

  };

  const changeEmail = (e) => {
    setEmail(e.target.value);
  };

  const changePassword = (e) => {
    setPwd(e.target.value);
  };

  function renderLoadingAuthMethods() {
    if (authMethodsReady) {
      return (<></>);
    }
    return (
        <div className="loadingcenter">
          <img src={loadingImage} alt={t("loading")} title={t("loading")} width="32" height="32" />
        </div>
    );
  }

  function renderLoginDisabled() {
    if (authMethodsReady && authMethods.length === 0) {
      return (
        <div className="warning">
          <p>Login is temporarily disabled. Please try again later.</p>
        </div>
      );
    }
    return (<></>);
  }

  function renderLoginWithPassword() {
    if (authMethodsReady && authMethods.includes('password')) {
      return (
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t("email_address")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                onChange={changeEmail}
                ref={emailRef}
                value={email}
                required
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="password">{t("password")}</Label>
                <Link
                  to="/forgotpassword"
                  className="ms-auto inline-block text-sm underline"
                >
                  {t("forgot_your_password")}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                onChange={changePassword}
                value={pwd}
                required
              />
            </div>
            {errMsg ? <div className="error">{errMsg}</div> : ""}
            <Button type="submit" className="w-full">
              {t("login")}
            </Button>
          </div>
        </form>
      );
    }
    return (<></>);
  }

  function renderLoginWithGoogle() {
    if (authMethodsReady && authMethods.includes('google')) {
      return (
          <div>
            {/* <img
              onClick={() =>
                window.open(`${config.API_BASE}/auth/redirect`, "_self")
              }
              style={{ margin: "0 auto", cursor: "pointer" }}
              src={signInGoogle}
              alt="Google login"
            /> */}
            <button
              className={`googlelogin${direction}`}
              onClick={() => {
                if (location.state) {
                  sessionStorage.setItem(
                    "locationstate",
                    JSON.stringify(location.state)
                  );
                }
                window.open(`${config.API_BASE}/auth/google/redirect`, "_self");
              }}
            >
              <img src={signInGoogle} alt="Google login" title="Google login" />
              {t("login_with_google")}
            </button>
          </div>
      );
    }
    return (<></>);
  }

  return (
    <div className="h-full w-full flex items-center justify-start py-12">
      <div className="mx-auto grid w-[350px] gap-6">
        <div className="grid gap-2 text-center">
          <h1 className="text-3xl font-bold">{t("login")}</h1>
        </div>
        {renderLoadingAuthMethods()}
        {renderLoginDisabled()}
        {renderLoginWithPassword()}
        {renderLoginWithGoogle()}
        <div className="mt-3 text-center text-sm">
          {t("dont_have_an_account")}
          <Link
            to="/register"
            state={
              location.state?.from
                ? { from: location.state?.from }
                : null
            }
            className="underline ms-2"
          >
            {t("sign_up")}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
